import { useEffect, useMemo } from 'react'
import tinycolor from 'tinycolor2'

import { usePartnerConfig } from '@percent/hosted-donation-gateway/hooks/usePartnerConfig/usePartnerConfig'
import { ThemeContextProps } from './ThemeProvider.types'
import { safeLocalStorage } from '@percent/hosted-donation-gateway/utils/storage'

const DEFAULT_PRIMARY_COLOR = '#42464F'

function getInitialState(): string {
  return safeLocalStorage.getItem('primary') || DEFAULT_PRIMARY_COLOR
}

const detectTextColor = (color: string): 'white' | 'dark' => (tinycolor(color).isDark() ? 'white' : 'dark')

const hexToRgb = (hex: string): string | null => {
  const color = tinycolor(hex)

  if (!color.isValid()) return null
  const { r, g, b } = color.toRgb()

  return `${r}, ${g}, ${b}`
}

export function ThemeContext({ children }: ThemeContextProps) {
  const { partnerConfig } = usePartnerConfig()

  useEffect(() => {
    if (partnerConfig?.branding.primaryColor) {
      safeLocalStorage.setItem('primary', partnerConfig.branding.primaryColor)
    }
  }, [partnerConfig?.branding.primaryColor])

  const partnerPrimaryColor = partnerConfig?.branding.primaryColor || getInitialState()

  const colorCalculations = useMemo(() => {
    const color = tinycolor(partnerPrimaryColor)

    if (!color.isValid()) return null

    const hoverColor = color.isDark() ? color.brighten(15).toString() : color.darken(15).toString()

    const rgb = hexToRgb(partnerPrimaryColor)
    const textColor = detectTextColor(partnerPrimaryColor)

    return {
      hoverColor,
      rgb,
      textColor
    }
  }, [partnerPrimaryColor])

  useEffect(() => {
    if (partnerPrimaryColor && colorCalculations) {
      document.documentElement.style.setProperty('--client-color', partnerPrimaryColor)
      document.documentElement.style.setProperty('--client-color-hover', colorCalculations.hoverColor)

      if (colorCalculations.rgb) {
        document.documentElement.style.setProperty('--client-color-rgb', colorCalculations.rgb)
      }

      document.documentElement.style.setProperty('--detected-text-color', colorCalculations.textColor)
    }
  }, [partnerPrimaryColor, colorCalculations])

  return <>{children}</>
}
